// A case handed from the Reviews lens to the trace workspace: a board of its
// own (traceCanvasStore openCaseCanvas) anchored on the session of its most
// pressing finding, its other sessions laid on beside it, its findings pinned
// open on their cards, Investigate's account of it as a note at the top, and
// each of its notes that names a moment pinned at that moment on its
// session's card, coloured for what it says (missed, matters, doesn't matter,
// corrects the check). The board opens over the page, so closing it lands the
// reader back on the case. Several cases can be laid out together on one
// board of their own (openCasesOnWorkspace).
import { addTraceNotes, removeTraceNotes } from "./traceNoteStore.js";
import { bindCanvasInvestigation, MAX_BOARD_SESSIONS, caseCanvasId, casesCanvasId, openCaseCanvas, traceCanvasSnapshot } from "./traceCanvasStore.js";
import { GROUP_LABEL } from "./investigateFacts.js";
import { investigationOf } from "./investigateStore.js";

const str = (v) => (v == null ? "" : String(v));
const clip = (v, n) => {
    const t = str(v).replace(/\s+/g, " ").trim();
    return t.length > n ? `${t.slice(0, n - 1).trimEnd()}\u2026` : t;
};
export const ACCOUNT_NOTE = "inv:account";
export const NOTE_KIND_LABEL = { matters: "Matters", doesnt: "Doesn't matter", missed: "Missed", corrects: "Corrects the check", note: "Note" };
const NEEDS_LABEL = { yes: "Follow up", no: "Can be let go", unsure: "Unclear from what was read" };
// Most findings a board pins open; the rest stay on their cards' moments.
const BOARD_FINDINGS = 40;

// The prefix every note this case pins carries, so a later hand-off replaces
// them rather than piling up.
export const caseNotePrefix = (caseId) => `inv:${caseId}:`;
export const investigateChatRef = (scopeKey) => ({ key: `investigate:${scopeKey || "org"}`, title: "Investigate" });

// The note at the top of the board: what the case is, what Investigate made
// of it, and its notes that name no moment.
export function caseAccountText(view) {
    const lines = [`Investigate: ${clip(view.title, 140)}`];
    if (view.account) lines.push(view.account);
    const groups = view.groups.map((g) => GROUP_LABEL[g] || g).join(", ");
    lines.push("", `${view.items.length} finding${view.items.length === 1 ? "" : "s"} in ${view.sessions.length} trace${view.sessions.length === 1 ? "" : "s"}: ${groups}.`);
    if (view.metBy.length) lines.push(`Met ${view.metBy.join(", ")}.`);
    if (view.dig?.status === "done" && view.dig.needsPerson) lines.push(`${NEEDS_LABEL[view.dig.needsPerson]}${view.dig.why ? `: ${view.dig.why}` : "."}`);
    const loose = (view.notes || []).filter((n) => !(n.session && n.messageId));
    if (loose.length) {
        lines.push("");
        for (const n of loose.slice(0, 8)) lines.push(`${NOTE_KIND_LABEL[n.kind] || "Note"}: ${clip(n.text, 300)}`);
    }
    if (view.related?.length) {
        lines.push("", "Related traces:");
        for (const r of view.related.slice(0, 6)) lines.push(`${r.session.slice(0, 8)}: ${clip(r.why, 160)}`);
    }
    return lines.join("\n").slice(0, 4000);
}

// The notes a case pins at their moments.
export function caseTraceNotes(view, scopeKey) {
    const chat = investigateChatRef(scopeKey);
    return (view.notes || [])
        .filter((n) => n.session && n.messageId)
        .map((n) => ({
            id: `${caseNotePrefix(view.id)}${n.id}`,
            session: n.session,
            messageId: n.messageId,
            text: n.quote ? `${clip(n.text, 400)}\n"${clip(n.quote, 180)}"` : clip(n.text, 560),
            ...(n.kind && n.kind !== "note" ? { kind: n.kind } : {}),
            chat,
            at: n.at || Date.now(),
        }));
}

// Where the board opens: the case's most pressing finding, at the first
// message it cites when it cites one (`<session>#<message>`, which the board
// picks as a click would), else at its session's card.
export function leadMoment(view) {
    const lead = view?.items?.[0]?.finding;
    if (!lead?.session) return null;
    const message = (lead.messages || []).find(Boolean);
    return message ? `${lead.session}#${message}` : lead.session;
}

const caseSessions = (view) => [...new Set([...(view.sessions || []), ...(view.related || []).map((r) => r.session)])];

// The case's notes pinned at their moments on its sessions, wherever they
// are drawn, in place of the ones it pinned before.
export function pinCaseNotes(scopeKey, view) {
    if (!view?.id) return;
    const sessions = [...new Set([...caseSessions(view), ...(view.notes || []).map((n) => n.session).filter(Boolean)])];
    removeTraceNotes(sessions, caseNotePrefix(view.id));
    addTraceNotes(caseTraceNotes(view, scopeKey));
}

// Hands the case over: its board kept and laid over the page (or only kept,
// with `open` false). `view` is the case as buildCases draws it, stored (not
// provisional); `titleOf(session)` names a card before its record is read.
export function openCaseOnWorkspace({ scopeKey, view, investigationId = null, focus = null, open = true, titleOf = null } = {}) {
    if (!view?.id) return null;
    const sessions = caseSessions(view);
    pinCaseNotes(scopeKey, view);
    const id = openCaseCanvas({
        id: caseCanvasId(scopeKey, view.id),
        title: clip(view.title, 140),
        anchor: view.items?.[0]?.finding?.session || sessions[0] || "",
        sessions: sessions.map((id) => ({ id, title: titleOf?.(id) || "" })),
        findings: view.items.slice(0, BOARD_FINDINGS).map((p) => ({ id: p.finding.id, session: p.finding.session, title: p.finding.title })),
        account: { id: ACCOUNT_NOTE, text: caseAccountText(view) },
        focus: focus || leadMoment(view),
        open,
    });
    if (id && investigationId) bindCanvasInvestigation(id, investigationId);
    return id;
}

// The note at the top of a board of several cases: each case in a line, in
// the order they were laid out. What digs made of each stays with the case.
export function casesAccountText(views) {
    const n = (k, one) => `${k} ${one}${k === 1 ? "" : "s"}`;
    return [
        `Investigate: ${views.length} cases`,
        ...views.map((v) => `${clip(v.title, 140)}: ${n(v.items.length, "finding")} in ${n(v.sessions.length, "trace")}`),
    ].join("\n").slice(0, 4000);
}

// The first of each list, then the second of each, and so on, `max` at most.
const inTurn = (lists, max) => {
    const out = [];
    for (let i = 0; out.length < max && lists.some((l) => i < l.length); i++) {
        for (const l of lists) if (i < l.length && out.length < max) out.push(l[i]);
    }
    return out;
};

/**
 * Several cases laid out together on one board, to read side by side; one
 * case opens its own board instead (openCaseOnWorkspace). The board is
 * anchored on the first case's lead finding, each case's sessions laid on in
 * the order given while it has room (MAX_BOARD_SESSIONS): a case that would
 * take it past that is left off whole. Their findings are pinned open a turn
 * each (every case's most pressing first), their notes pinned at their
 * moments, and a note at the top names them. The board is none of the cases'
 * own (`cases:<scope>:<key>`), so a session laid on it joins none of them.
 * Returns { id, opened, left }: the cases on the board and those left off.
 */
export function openCasesOnWorkspace({ scopeKey, views = [], open = true, titleOf = null } = {}) {
    const seen = new Set();
    const onBoard = new Set();
    const opened = [];
    const left = [];
    for (const view of views) {
        if (!view?.id || seen.has(view.id)) continue;
        seen.add(view.id);
        const more = caseSessions(view).map((s) => str(s).toLowerCase()).filter((s) => s && !onBoard.has(s));
        if (opened.length && onBoard.size + more.length > MAX_BOARD_SESSIONS) {
            left.push(view);
            continue;
        }
        for (const s of more) onBoard.add(s);
        opened.push(view);
    }
    if (opened.length < 2) return { id: opened.length ? openCaseOnWorkspace({ scopeKey, view: opened[0], open, titleOf }) : null, opened, left };
    for (const view of opened) pinCaseNotes(scopeKey, view);
    const [lead] = opened;
    const others = opened.length - 1;
    const id = openCaseCanvas({
        id: casesCanvasId(scopeKey, opened.map((v) => v.id)),
        title: `${clip(lead.title, 100)} and ${others} more case${others === 1 ? "" : "s"}`,
        anchor: lead.items?.[0]?.finding?.session || [...onBoard][0],
        sessions: [...onBoard].map((s) => ({ id: s, title: titleOf?.(s) || "" })),
        findings: inTurn(opened.map((v) => v.items || []), BOARD_FINDINGS).map((p) => ({ id: p.finding.id, session: p.finding.session, title: p.finding.title })),
        account: { id: ACCOUNT_NOTE, text: casesAccountText(opened) },
        cases: opened.map((v) => v.id),
        focus: leadMoment(lead),
        open,
    });
    return { id, opened, left };
}

// The case `caseId` that `scope` keeps, or null.
const caseIn = (scope, caseId) => (scope && caseId ? investigationOf(scope).cases.find((c) => c.id === caseId) || null : null);

/**
 * The cases a board was opened for, as their scope keeps them (a case's
 * board, or several cases laid out together); none for any other board.
 * Scope keys and case ids both hold colons, so a case's board id is read
 * against the cases each way of splitting it names.
 */
export function boardCases(boardId) {
    const id = str(boardId);
    if (id.startsWith("case:")) {
        const rest = id.slice("case:".length);
        for (let at = rest.indexOf(":"); at > 0; at = rest.indexOf(":", at + 1)) {
            const hit = caseIn(rest.slice(0, at), rest.slice(at + 1));
            if (hit) return [hit];
        }
        return [];
    }
    if (id.startsWith("cases:")) {
        const scope = id.slice("cases:".length, id.lastIndexOf(":"));
        return (traceCanvasSnapshot(id).cases || []).map((c) => caseIn(scope, c)).filter(Boolean);
    }
    return [];
}
